import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "~/lib/utils";
import { Button } from "~/components/ui/button";
import type { ButtonIconComponent } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Textarea } from "~/components/ui/textarea";
import { useShape } from "~/lib/shape-context";

function InputGroup({ className, ...props }: React.ComponentProps<"div">) {
  const shape = useShape();

  return (
    <div
      data-slot="input-group"
      role="group"
      className={cn(
        "control-surface group/input-group relative flex h-11 w-full min-w-0 items-center rounded-4xl transition-[color,box-shadow,background-color] duration-80 outline-none in-data-[slot=combobox-content]:focus-within:ring-0 has-data-[align=block-end]:rounded-3xl has-data-[align=block-start]:rounded-3xl has-[[data-slot=input-group-control]:focus-visible]:ring-1 has-[[data-slot=input-group-control]:focus-visible]:ring-ring has-[[data-slot][aria-invalid=true]]:ring-1 has-[[data-slot][aria-invalid=true]]:ring-destructive/50 has-[textarea]:rounded-2xl has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>textarea]:h-auto has-[>[data-align=block-end]]:[&>input]:pt-3 has-[>[data-align=block-start]]:[&>input]:pb-3 has-[>[data-align=inline-end]]:[&>input]:pr-1.5 has-[>[data-align=inline-start]]:[&>input]:pl-1.5",
        shape.input,
        className,
      )}
      {...props}
    />
  );
}

const inputGroupAddonVariants = cva(
  "flex h-auto cursor-text items-center justify-center gap-2 py-2 text-meta font-medium text-muted-foreground select-none group-data-[disabled=true]/input-group:opacity-50 **:data-[slot=kbd]:rounded-3xl **:data-[slot=kbd]:bg-muted-foreground/10 **:data-[slot=kbd]:px-1.5 [&>svg:not([class*='size-'])]:size-4",
  {
    variants: {
      align: {
        "inline-start": "order-first pl-3 has-[>button]:-ml-1 has-[>kbd]:-ml-1",
        "inline-end": "order-last pr-3 has-[>button]:-mr-1 has-[>kbd]:-mr-1",
        "block-start":
          "order-first w-full justify-start px-3 pt-3 group-has-[>input]/input-group:pt-3.5 [.border-b]:pb-3.5",
        "block-end":
          "order-last w-full justify-start px-3 pb-3 group-has-[>input]/input-group:pb-3.5 [.border-t]:pt-3.5",
      },
    },
    defaultVariants: {
      align: "inline-start",
    },
  },
);

function InputGroupAddon({
  className,
  align = "inline-start",
  onMouseDown,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      onMouseDown={(event) => {
        onMouseDown?.(event);
        if (event.defaultPrevented) {
          return;
        }

        if ((event.target as HTMLElement).closest("button")) {
          return;
        }

        event.preventDefault();
        event.currentTarget.parentElement?.querySelector("input")?.focus();
      }}
      {...props}
    />
  );
}

const inputGroupButtonVariants = cva(
  "flex items-center gap-2 rounded-4xl text-meta shadow-none",
  {
    variants: {
      size: {
        xs: "h-7 gap-1 rounded-xl px-1.5 [&>svg:not([class*='size-'])]:size-3.5",
        sm: "h-8 px-2.5",
        "icon-xs": "size-7 rounded-xl p-0 has-[>svg]:p-0",
        "icon-sm": "size-8 p-0 has-[>svg]:p-0",
      },
    },
    defaultVariants: {
      size: "xs",
    },
  },
);

function InputGroupButton({
  className,
  type = "button",
  variant = "ghost",
  size = "xs",
  ...props
}: Omit<React.ComponentProps<typeof Button>, "size" | "type"> &
  VariantProps<typeof inputGroupButtonVariants> & {
    type?: "button" | "submit" | "reset";
}) {
  const shape = useShape();

  return (
    <Button
      type={type}
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), shape.button, className)}
      {...props}
    />
  );
}

function InputGroupText({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      className={cn(
        "flex items-center gap-2 text-body text-muted-foreground [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

function InputGroupInput({
  className,
  ...props
}: React.ComponentProps<"input">) {
  return (
    <Input
      data-slot="input-group-control"
      className={cn(
        "h-full flex-1 rounded-none border-0 bg-transparent text-body !shadow-none ring-0 focus-visible:border-transparent focus-visible:bg-transparent focus-visible:ring-0 aria-invalid:ring-0 dark:bg-transparent dark:focus-visible:bg-transparent",
        className,
      )}
      {...props}
    />
  );
}

function InputGroupTextarea({
  className,
  ...props
}: React.ComponentProps<"textarea">) {
  return (
    <Textarea
      data-slot="input-group-control"
      className={cn(
        "flex-1 resize-none rounded-none border-0 bg-transparent py-2.5 shadow-none ring-0 focus-visible:ring-0 aria-invalid:ring-0 dark:bg-transparent",
        className,
      )}
      {...props}
    />
  );
}

interface InputFieldProps
  extends Omit<React.ComponentProps<typeof InputGroupInput>, "onChange"> {
  description?: React.ReactNode;
  error?: React.ReactNode;
  icon?: ButtonIconComponent;
  index?: number;
  label?: React.ReactNode;
  onChange?: (value: string) => void;
  rootClassName?: string;
}

function InputField({
  className,
  description,
  error,
  icon: Icon,
  id,
  index,
  label,
  onChange,
  rootClassName,
  ...props
}: InputFieldProps) {
  const reactId = React.useId();
  const resolvedId = id ?? reactId;

  return (
    <div
      data-index={index}
      data-slot="input-field"
      className={cn("grid gap-1.5", rootClassName)}
    >
      {label ? (
        <label
          className="w-fit text-meta font-medium text-foreground"
          htmlFor={resolvedId}
        >
          {label}
        </label>
      ) : null}
      <InputGroup>
        {Icon ? (
          <InputGroupAddon align="inline-start">
            <Icon aria-hidden={true} className="size-4" strokeWidth={1.75} />
          </InputGroupAddon>
        ) : null}
        <InputGroupInput
          aria-invalid={error ? true : props["aria-invalid"]}
          className={className}
          id={resolvedId}
          onChange={(event) => onChange?.(event.currentTarget.value)}
          {...props}
        />
      </InputGroup>
      {description ? (
        <p className="text-left text-meta font-normal text-muted-foreground">
          {description}
        </p>
      ) : null}
      {error ? (
        <p className="text-meta font-medium text-status-negative-foreground">{error}</p>
      ) : null}
    </div>
  );
}

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputField,
  InputGroupText,
  InputGroupInput,
  InputGroupTextarea,
};
export type { InputFieldProps };
